import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import axios from 'axios';

const KIND_LABEL = { entry: 'Entry Survey', exit: 'Exit Survey' };

// Student-facing Entry / Exit survey (link copied from Admin Panel → Surveys)
const SurveyPage = () => {
  const { programId, kind } = useParams();
  const token = useSelector(state => state.auth.token);
  const [survey, setSurvey] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [answers, setAnswers] = useState({});
  const [errors, setErrors] = useState({});
  const [submitMessage, setSubmitMessage] = useState('');

  useEffect(() => {
    axios.get(`/api/program-surveys/${programId}/${kind}/take`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => setSurvey(res.data))
      .catch(err => setLoadError(err.response?.data?.error || 'The survey isn’t available right now'));
  }, [programId, kind, token]);

  const isAnswered = (q) => {
    const v = answers[q._id];
    return !(v === undefined || v === null || (typeof v === 'string' && !v.trim()) || (Array.isArray(v) && v.length === 0));
  };

  const setAnswer = (questionId, value) => {
    setAnswers({ ...answers, [questionId]: value });
    if (errors[questionId]) setErrors({ ...errors, [questionId]: undefined });
  };

  const toggleChoice = (questionId, option) => {
    const current = Array.isArray(answers[questionId]) ? answers[questionId] : [];
    setAnswer(questionId, current.includes(option) ? current.filter(o => o !== option) : [...current, option]);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const missing = {};
    survey.questions.forEach(q => {
      if (q.required && !isAnswered(q)) missing[q._id] = q.type === 'rating' ? 'Choose a number from 1 to 5.' : 'This question is required.';
    });
    setErrors(missing);
    if (Object.keys(missing).length) {
      setSubmitMessage(`Answer the ${Object.keys(missing).length} highlighted question(s) to submit.`);
      document.getElementById(`q-${Object.keys(missing)[0]}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    // Saving answers and the spider-chart results are the next step
    setSubmitMessage('Preview only: answers are not saved yet. Results with the spider chart are coming next.');
  };

  const pageClass = 'w-full flex-1 bg-gray-50 dark:bg-background';
  const containerClass = 'w-full max-w-2xl mx-auto px-4 py-8 sm:py-12';
  const cardClass = 'bg-white dark:bg-card border border-gray-200 dark:border-border rounded-2xl shadow-sm';

  if (loadError) {
    return (
      <div className={pageClass}>
        <div className={containerClass}>
          <div className={`${cardClass} p-8 text-center`}>
            <h2 className="text-xl font-bold text-gray-900 dark:text-foreground mb-2">{KIND_LABEL[kind] || 'Survey'}</h2>
            <p className="text-gray-500 dark:text-foreground/60">{loadError}</p>
          </div>
        </div>
      </div>
    );
  }

  if (!survey) {
    return (
      <div className={pageClass}>
        <div className={`${containerClass} space-y-4`}>
          <div className="h-10 w-2/3 bg-gray-200 dark:bg-muted rounded-lg animate-pulse" />
          <div className="h-5 w-full bg-gray-200 dark:bg-muted rounded animate-pulse" />
          <div className="h-40 w-full bg-gray-200 dark:bg-muted rounded-2xl animate-pulse" />
        </div>
      </div>
    );
  }

  const total = survey.questions.length;
  const done = survey.questions.filter(isAnswered).length;
  const pct = total ? Math.round((done / total) * 100) : 0;
  const hasErrors = Object.keys(errors).some(k => errors[k]);

  return (
    <div className={pageClass}>
      <div className={containerClass}>
        {survey.isPreview && (
          <div className="mb-6 flex items-start gap-2 px-4 py-3 rounded-xl bg-amber-50 border border-amber-200 text-amber-800 text-sm dark:bg-amber-900/20 dark:border-amber-800 dark:text-amber-200">
            <span aria-hidden="true">👁</span>
            <span>
              Admin preview{kind === 'exit' && !survey.isOpen ? ', closed to students right now' : ''}. Answers are not saved.
            </span>
          </div>
        )}

        {/* Header */}
        <header className="mb-8">
          {survey.program?.title && (
            <p className="text-xs font-semibold uppercase tracking-wider text-[#1086a4] mb-2">{survey.program.title}</p>
          )}
          <h1 className="text-3xl sm:text-4xl font-extrabold text-gray-900 dark:text-foreground mb-3">{KIND_LABEL[kind]}</h1>
          {survey.intro && <p className="text-gray-600 dark:text-foreground/70 text-base sm:text-lg leading-relaxed">{survey.intro}</p>}
        </header>

        {total === 0 ? (
          <div className={`${cardClass} p-8 text-center text-gray-500 dark:text-foreground/60`}>
            This survey has no questions yet.
          </div>
        ) : (
          <form onSubmit={handleSubmit} noValidate>
            {/* Progress */}
            <div className={`${cardClass} px-5 py-4 mb-4`}>
              <div className="flex items-center justify-between text-sm mb-2">
                <span className="text-gray-600 dark:text-foreground/70">
                  <strong className="text-gray-900 dark:text-foreground">{done}</strong> of {total} answered
                </span>
                <span className="font-semibold text-[#1086a4]">{pct}%</span>
              </div>
              <div className="h-2 bg-gray-100 dark:bg-muted rounded-full overflow-hidden">
                <div className="h-full bg-[#1086a4] rounded-full transition-all duration-300" style={{ width: `${pct}%` }} />
              </div>
            </div>

            {/* Questions */}
            <div className="space-y-4">
              {survey.questions.map((q, index) => {
                const answered = isAnswered(q);
                return (
                  <div
                    key={q._id}
                    id={`q-${q._id}`}
                    className={`${cardClass} p-5 sm:p-6 transition-colors ${errors[q._id] ? 'border-red-300 dark:border-red-700 ring-1 ring-red-200 dark:ring-red-900' : ''}`}
                  >
                    <div className="flex items-start gap-3 mb-4">
                      <span className={`shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-colors ${answered ? 'bg-[#1086a4] text-white' : 'bg-[#1086a4]/10 text-[#1086a4]'}`}>
                        {answered ? '✓' : index + 1}
                      </span>
                      <div className="min-w-0">
                        <p className="font-semibold text-gray-900 dark:text-foreground text-base sm:text-lg leading-snug">
                          {q.text}
                          {!q.required && <span className="text-gray-400 font-normal text-sm ml-2">Optional</span>}
                        </p>
                        {q.help && <p className="text-gray-500 dark:text-foreground/60 text-sm mt-1">{q.help}</p>}
                      </div>
                    </div>

                    {q.type === 'rating' && (
                      <div role="radiogroup" aria-label={q.text}>
                        <div className="grid grid-cols-5 gap-2 sm:gap-3">
                          {q.options.map(value => {
                            const selected = String(answers[q._id]) === String(value);
                            return (
                              <button
                                key={value}
                                type="button"
                                role="radio"
                                aria-checked={selected}
                                onClick={() => setAnswer(q._id, value)}
                                className={`h-12 sm:h-14 rounded-xl border-2 text-lg font-bold transition-all ${selected
                                  ? 'bg-[#1086a4] border-[#1086a4] text-white shadow-md scale-[1.03]'
                                  : 'bg-white dark:bg-background border-gray-200 dark:border-border text-gray-600 dark:text-foreground/70 hover:border-[#1086a4] hover:text-[#1086a4]'}`}
                              >
                                {value}
                              </button>
                            );
                          })}
                        </div>
                        {(q.scaleMinLabel || q.scaleMaxLabel) && (
                          <div className="flex justify-between gap-4 text-xs text-gray-500 dark:text-foreground/50 mt-2">
                            <span>{q.scaleMinLabel}</span>
                            <span className="text-right">{q.scaleMaxLabel}</span>
                          </div>
                        )}
                      </div>
                    )}

                    {q.type === 'short_answer' && (
                      <input
                        type="text"
                        maxLength={300}
                        value={answers[q._id] || ''}
                        onChange={(e) => setAnswer(q._id, e.target.value)}
                        placeholder="Your answer"
                        className="w-full px-4 py-3 border border-gray-200 dark:border-border rounded-xl bg-gray-50 dark:bg-background text-gray-900 dark:text-foreground placeholder-gray-400 focus:outline-none focus:bg-white focus:border-[#1086a4] focus:ring-2 focus:ring-[#1086a4]/20 transition-colors"
                      />
                    )}

                    {q.type === 'long_answer' && (
                      <textarea
                        rows={4}
                        maxLength={5000}
                        value={answers[q._id] || ''}
                        onChange={(e) => setAnswer(q._id, e.target.value)}
                        placeholder="Your answer"
                        className="w-full px-4 py-3 border border-gray-200 dark:border-border rounded-xl bg-gray-50 dark:bg-background text-gray-900 dark:text-foreground placeholder-gray-400 focus:outline-none focus:bg-white focus:border-[#1086a4] focus:ring-2 focus:ring-[#1086a4]/20 transition-colors resize-y"
                      />
                    )}

                    {(q.type === 'multiple_choice' || q.type === 'multiple_select') && (
                      <div className="space-y-2">
                        {q.type === 'multiple_select' && <p className="text-xs text-gray-500 dark:text-foreground/50 -mt-2 mb-2">Choose all that apply</p>}
                        {q.options.map(option => {
                          const multiple = q.type === 'multiple_select';
                          const checked = multiple ? (answers[q._id] || []).includes(option) : answers[q._id] === option;
                          return (
                            <button
                              key={option}
                              type="button"
                              role={multiple ? 'checkbox' : 'radio'}
                              aria-checked={checked}
                              onClick={() => (multiple ? toggleChoice(q._id, option) : setAnswer(q._id, option))}
                              className={`w-full flex items-center gap-3 px-4 py-3 border-2 rounded-xl text-left transition-colors ${checked
                                ? 'border-[#1086a4] bg-[#1086a4]/5'
                                : 'border-gray-200 dark:border-border hover:border-[#1086a4]/50 bg-white dark:bg-background'}`}
                            >
                              <span className={`shrink-0 w-5 h-5 flex items-center justify-center border-2 transition-colors ${multiple ? 'rounded-md' : 'rounded-full'} ${checked ? 'border-[#1086a4] bg-[#1086a4]' : 'border-gray-300 dark:border-border'}`}>
                                {checked && (multiple
                                  ? <span className="text-white text-xs leading-none">✓</span>
                                  : <span className="w-2 h-2 rounded-full bg-white" />)}
                              </span>
                              <span className={`text-gray-800 dark:text-foreground ${checked ? 'font-medium' : ''}`}>{option}</span>
                            </button>
                          );
                        })}
                      </div>
                    )}

                    {errors[q._id] && <p className="text-red-600 dark:text-red-400 text-sm mt-3">{errors[q._id]}</p>}
                  </div>
                );
              })}
            </div>

            {/* Submit */}
            <div className="mt-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <p className={`text-sm ${hasErrors ? 'text-red-600 dark:text-red-400' : 'text-[#1086a4]'}`}>{submitMessage}</p>
              <button
                type="submit"
                className="w-full sm:w-auto shrink-0 px-8 py-3 bg-[#1086a4] hover:bg-[#0d6e87] text-white font-semibold rounded-xl shadow-sm transition-colors"
              >
                {kind === 'entry' ? 'Submit and see my results' : 'Submit and see my growth'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default SurveyPage;
